// Faza 3 sledzenia pozycji: abstrakcja nad usługami STT (speech-to-text).
// Trzy warstwy z architektury (spec-stt-faza3.md): ElevenLabs Scribe (siec,
// najlepsza jakosc), Whisper na GPU tailnetu (bez internetu), a gdy obie
// zawiedza - `transcribe()` wraca `null` i wolajacy (`mikrofon.ts`) po prostu
// nie dostaje sekcji z STT, chromagram dziala dalej sam.

/** Konfiguracja warstw STT. `null` wylacza dana warstwe (np. brak klucza
 * ElevenLabs wklejonego w ustawieniach). */
export interface SttConfig {
  elevenLabsKey: string | null;
  whisperUrl: string | null;
}

export interface SttResult {
  text: string;
  source: 'elevenlabs' | 'whisper';
}

const TIMEOUT_MS = 8000;

/** `fetch` z timeoutem: po `TIMEOUT_MS` przerywa żądanie i rzuca, żeby jeden
 * zawieszony serwis STT nie zablokował na zawsze próby kolejnej warstwy. */
async function fetchZTimeoutem(url: string, init: RequestInit): Promise<Response> {
  const kontroler = new AbortController();
  const idTimeoutu = setTimeout(() => kontroler.abort(), TIMEOUT_MS);
  try {
    return await fetch(url, { ...init, signal: kontroler.signal });
  } finally {
    clearTimeout(idTimeoutu);
  }
}

/** ElevenLabs Scribe v2. Zwraca `null` przy jakimkolwiek błędzie (sieć,
 * status, brak `text` w odpowiedzi) - wolajacy potem probuje Whisper. */
async function transcribeElevenLabs(audioBlob: Blob, key: string): Promise<string | null> {
  try {
    const dane = new FormData();
    dane.append('file', audioBlob, 'audio.webm');
    dane.append('model_id', 'scribe_v2');
    dane.append('language_code', 'pol');

    const odpowiedz = await fetchZTimeoutem('https://api.elevenlabs.io/v1/speech-to-text', {
      method: 'POST',
      headers: { 'xi-api-key': key },
      body: dane,
    });
    if (!odpowiedz.ok) return null;

    const wynik = (await odpowiedz.json()) as { text?: unknown };
    return typeof wynik.text === 'string' ? wynik.text : null;
  } catch {
    return null;
  }
}

/** Whisper na serwerze tailnetu. Body to surowy blob audio (webm/opus),
 * `Content-Type` mowi serwerowi, jak go zdekodowac. */
async function transcribeWhisper(audioBlob: Blob, url: string): Promise<string | null> {
  try {
    const odpowiedz = await fetchZTimeoutem(url, {
      method: 'POST',
      headers: { 'Content-Type': 'audio/webm' },
      body: audioBlob,
    });
    if (!odpowiedz.ok) return null;

    const wynik = (await odpowiedz.json()) as { text?: unknown };
    return typeof wynik.text === 'string' ? wynik.text : null;
  } catch {
    return null;
  }
}

/**
 * Probuje przepisac `audioBlob` na tekst: najpierw ElevenLabs (gdy podano
 * klucz), potem Whisper (gdy podano URL). Pierwszy sukces wygrywa. Gdy obie
 * warstwy zawiodly albo nie sa skonfigurowane, wraca `null` - to NIE jest
 * blad, to sygnal dla wolajacego, ze STT nie dalo sekcji tym razem.
 */
export interface AlignResult {
  lineIndex: number;
  confidence: number;
  transcript: string;
  source: 'whisper-align' | 'elevenlabs';
  /** 0..1, jak daleko w wersie padło ostatnie dopasowane słowo. */
  lineProgress?: number;
  /** Ile sekund przed końcem nagrania padło to słowo. */
  secondsAgo?: number;
  /** Daleki wers odrzucony przez strażnika skoku na serwerze, do potwierdzenia
   * drugim oknem (`potwierdzKandydata`). */
  candidate?: number;
  candidateProgress?: number;
}

/** Forced alignment: wysyla audio + tekst na serwer, dostaje indeks linii. */
export async function alignAudio(
  audioBlob: Blob,
  lyricsLines: readonly string[],
  config: SttConfig,
  lastLine: number = -1,
): Promise<AlignResult | null> {
  if (config.whisperUrl) {
    try {
      const dane = new FormData();
      const ext = audioBlob.type.includes('wav') ? '.wav'
        : audioBlob.type.includes('mp4') ? '.m4a' : audioBlob.type.includes('ogg') ? '.ogg' : '.webm';
      dane.append('audio', audioBlob, `audio${ext}`);
      dane.append('lyrics', lyricsLines.join('\n'));
      dane.append('lastLine', String(lastLine));
      const odp = await fetchZTimeoutem(`${config.whisperUrl}/align`, {
        method: 'POST',
        body: dane,
      });
      if (odp.ok) {
        const wynik = (await odp.json()) as {
          lineIndex?: number | null; confidence?: number; transcript?: string;
          lineProgress?: number; secondsAgo?: number; candidate?: number; candidateProgress?: number;
        };
        // Serwer odpowiedział, ale bez pewnego miejsca: zwracamy samą
        // transkrypcję (lineIndex -1), żeby wołający mógł pokazać, co słychać,
        // i NIE pytamy ElevenLabs, bo sieć działa, tylko śpiew był niewyraźny.
        return {
          lineIndex: typeof wynik.lineIndex === 'number' ? wynik.lineIndex : -1,
          confidence: wynik.confidence ?? 0,
          transcript: wynik.transcript ?? '',
          source: 'whisper-align',
          ...(typeof wynik.lineProgress === 'number' ? { lineProgress: wynik.lineProgress } : {}),
          ...(typeof wynik.secondsAgo === 'number' ? { secondsAgo: wynik.secondsAgo } : {}),
          ...(typeof wynik.candidate === 'number' ? { candidate: wynik.candidate } : {}),
          ...(typeof wynik.candidateProgress === 'number' ? { candidateProgress: wynik.candidateProgress } : {}),
        };
      }
    } catch { /* fallback na ElevenLabs */ }
  }

  if (config.elevenLabsKey) {
    const text = await transcribeElevenLabs(audioBlob, config.elevenLabsKey);
    if (text && text.trim() !== '') {
      return { lineIndex: -1, confidence: 0, transcript: text, source: 'elevenlabs' };
    }
  }

  return null;
}

export async function transcribe(audioBlob: Blob, config: SttConfig): Promise<SttResult | null> {
  if (config.elevenLabsKey) {
    const text = await transcribeElevenLabs(audioBlob, config.elevenLabsKey);
    if (text && text.trim() !== '') return { text, source: 'elevenlabs' };
  }

  if (config.whisperUrl) {
    const text = await transcribeWhisper(audioBlob, config.whisperUrl);
    if (text && text.trim() !== '') return { text, source: 'whisper' };
  }

  return null;
}
